// Focus pins, as pure logic (D1-c §3.3, handoff §6.3).
//
// A focus IS the pin. It does not copy the register row, it NAMES it — by brain
// action id, or by Jira key — and that naming is what makes a tick in the rail
// write through to the row. Everything in this file is about keeping that
// identity honest:
//
//   · `pinMatchesRow` decides whether a row already sits in Today, which drives
//     both the `+`/✓ state of the pull circle and the un-pin path.
//   · `focusFromRow` builds the pin, stamping `firstPulledAt` — the ONLY home
//     the carry counter has (handoff §8.1).
//   · `buildReconRows` decides what the morning strip asks about: yesterday's
//     pins that are neither done nor already reconciled, plus anything still
//     carried from before it.
//
// No React, no fetching — so all of it is unit-testable.

import type { BoardMember, Focus } from "../lib/api";
import type { RegisterRow } from "./registerRows";
import type { ReconRow } from "./TodayRail";

/** New pin id. Server-side ids are ULIDs; the client only needs uniqueness. */
export function newFocusId(): string {
  return `f-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Does this pin point at this register row? Keys are compared case-insensitively
 * because `jiraIssueKey` on a brain action is hand-typed free text, and a pin
 * created from a `ticketKey` must still recognise its Support row.
 */
export function pinMatchesRow(focus: Focus, row: RegisterRow): boolean {
  if (focus.actionId && row.actions.some((a) => a.id === focus.actionId)) return true;
  const key = row.issueKey?.trim().toUpperCase();
  if (!key) return false;
  const pinned = (focus.issueKey || focus.ticketKey || "").trim().toUpperCase();
  return !!pinned && pinned === key;
}

/** Turn a register row into the pin that represents it in Today. */
export function focusFromRow(
  row: RegisterRow,
  projectId: string,
  today: string,
  id: string = newFocusId(),
): Focus {
  const action = row.actions[0];
  return {
    id,
    text: row.summary,
    done: false,
    projectId,
    ...(row.issueKey ? { issueKey: row.issueKey, registerRef: row.issueKey } : {}),
    ...(action ? { actionId: action.id } : {}),
    firstPulledAt: today,
    ...(row.priority ? { priority: row.priority } : {}),
  };
}

/**
 * What the morning reconciliation strip asks about. `done` and `resolvedAs`
 * both mean finished: a parked or returned pin has deliberately left the day
 * and must never come back asking again — that is the entire point of Park.
 */
export function buildReconRows(
  member: Pick<BoardMember, "yesterday" | "carried">,
  nameOf: (projectId?: string) => string,
): ReconRow[] {
  const rows: ReconRow[] = [];
  const unresolved = (f: Focus) => !f.done && !f.resolvedAs;
  const yesterday = member.yesterday;
  if (yesterday) {
    for (const f of yesterday.focuses) {
      if (unresolved(f)) rows.push({ focus: f, date: yesterday.date, projectName: nameOf(f.projectId) });
    }
  }
  for (const f of member.carried ?? []) {
    if (unresolved(f)) rows.push({ focus: f, date: f.date, projectName: nameOf(f.projectId) });
  }
  return rows;
}
